import { FormGroupHeader } from "@/components/shared";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { ChatInputBar, MarginChatBubble, MarginDecisionOverruleDialog, MarginDecisionWithdrawDialog, PendingDecisionBar } from "./Components";
import { useTranslation } from "react-i18next";
import { JSX, useEffect, useRef } from "react";
import { MarginDecision, MarginDecisionEnum, MarginDecisionGroupFragment, MarginDecisionGroupPermissionEnum, TenderState, UserOnEntity, FetchUsersOnTenderQuery, FetchUsersOnTenderDocument, FetchUsersOnTenderQueryVariables } from "@/graphql/__generated__/graphql";
import { useFetchMarginDecisionsByGroupPolling } from "@/hooks/queries";
import { useParams } from "react-router-dom";
import { useMarginDecisionGroupData } from "@/hooks/cache";
import useUserStore from "@/store/shield";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";
import { useQuery } from "@apollo/client/react";

/**
 * MarginDecisionsComponent is a React functional component that renders the margin decision screen.
 * It fetches margin decisions by group and displays them in a chat-like interface.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <MarginDecisionsComponent />
 *
 * @hook
 * - `useTranslation` - For internationalization.
 * - `useParams` - To get the margin decision group ID from the URL.
 * - `useFetchMarginDecisionsByGroupQuery` - To fetch margin decisions by group.
 * - `useMarginDecisionGroupData` - To fetch margin decision group data from cache.
 * - `useEffect` - To scroll to the bottom of the chat area when the component loads or margin decisions data changes.
 *
 * @returns {JSX.Element} The rendered component.
 */
const MarginDecisionsComponent = (): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const scrollEndRef = useRef<HTMLDivElement>(null);
  const { marginDecisionGroupID, tenderID } = useParams<{ marginDecisionGroupID: string, tenderID: string }>();

  // Queries
  const { data, loading, useRefetch, stopPolling } = useFetchMarginDecisionsByGroupPolling(marginDecisionGroupID as string, tenderID as string);
  const marginDecisionGroupData = useMarginDecisionGroupData({ id: marginDecisionGroupID as string }) as MarginDecisionGroupFragment;
  const { data: usersOnTenderData } = useQuery<FetchUsersOnTenderQuery, FetchUsersOnTenderQueryVariables>(FetchUsersOnTenderDocument, {
    variables: {
      tenderId: Number(tenderID)
    }
  });

  // Constants
  const tenderUsers = usersOnTenderData?.fetchUsersOnTender || [];
  const userId = useUserStore((state) => state.userId);
  const hasFinalDecision = marginDecisionGroupData?.awaitingOrganizationId === null;
  const marginDecisions = data?.fetchMarginDecisionsByGroup?.marginDecisions as MarginDecision[];
  const groupPermissions = marginDecisionGroupData?.permissions;
  const canOverrule = groupPermissions?.includes(MarginDecisionGroupPermissionEnum.Overrule);
  const canWithdraw = groupPermissions?.includes(MarginDecisionGroupPermissionEnum.Withdraw);
  const autoApproved = marginDecisionGroupData?.autoApproved;
  const userHasPermission = [
    MarginDecisionGroupPermissionEnum.Decide,
    MarginDecisionGroupPermissionEnum.DecideAheadOfTurn,
    MarginDecisionGroupPermissionEnum.Overrule,
    MarginDecisionGroupPermissionEnum.Comment
  ].some(permission => groupPermissions?.includes(permission));

  // Stop polling if a uninamous decision has been reached
  useEffect(() => {
    if (hasFinalDecision) stopPolling();
  }, [hasFinalDecision]);

  /**
   * Aligns the chat bubble based on the margin decision's action and type.
   *
   * @function alignChatBubble
   * @param {MarginDecision} marginDecision - The margin decision to evaluate.
   * @returns {string} - The CSS alignment class for the chat bubble.
   */
  const alignChatBubble = (marginDecision: MarginDecision): string => {
    // Align to end if the current user made the comment
    if (marginDecision.decision === MarginDecisionEnum.Comment &&
      marginDecision.decidedBy === userId
    ) {
      return "justify-end";
    }
    if (marginDecision.decision === MarginDecisionEnum.Request) return "justify-center";
    if (marginDecision.decision === MarginDecisionEnum.Approve) return "justify-end";
    else return "";
  };

  useEffect(() => {
    // Scroll to bottom when component loads or data changes
    if (scrollEndRef.current) {
      // Use a small timeout to ensure DOM is fully rendered before scrolling
      setTimeout(() => {
        scrollEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [data?.fetchMarginDecisionsByGroup]); // Run effect whenever the margin decisions data changes

  // useEffect to handle initial load and ensure scroll works on navigation (from NotificationCenter)
  useEffect(() => {
    // Force scroll to bottom on initial load
    const scrollToBottom = () => {
      if (scrollEndRef.current) {
        scrollEndRef.current.scrollIntoView({ behavior: "auto" });
      }
    };

    // Initial scroll
    scrollToBottom();

    // Also scroll after a short delay to ensure content is fully loaded
    const timer = setTimeout(scrollToBottom, 300);

    return () => clearTimeout(timer);
  }, []);

  const actionButtons: ActionButtonConfig[] = [
    {
      text: <ArrowPathIcon className="icon-sm text-gray-800 group-hover:animate-spin" />,
      variant: "outline",
      size: "sm",
      className: "group active:scale-95",
      actionFn: () => {
        useRefetch(data?.fetchMarginDecisionsByGroup.tenderState as TenderState);
      },
    },
  ].filter(Boolean) as ActionButtonConfig[];

  return (
    <div className="flex flex-col gap-2 h-full pb-[100px]">
      {/* Main Margin Decisions Screen */}
      <FormGroupHeader
        className="mb-0 pr-4 pl-2"
        title={t("common:margindecisions")}
        actionButtons={actionButtons}
      >
        <div className="flex gap-2">
          {canWithdraw ? <MarginDecisionWithdrawDialog hasFinalDecision={hasFinalDecision} /> : undefined}
          {canOverrule ? <MarginDecisionOverruleDialog hasFinalDecision={hasFinalDecision} /> : undefined}
        </div>
      </FormGroupHeader>
      <ScrollArea className="grow pb-4 pr-4 pl-2 min-h-0">
        <div className="space-y-4 h-full">
          {marginDecisions && marginDecisions?.length > 0 ? (
            <>
              {marginDecisions.map((marginDecision) => {
                return (
                  <div key={`margin-decision-${marginDecision.id}`} className={cn("flex", alignChatBubble(marginDecision as any))}>
                    <MarginChatBubble
                      marginDecision={marginDecision as MarginDecision}
                      autoApproved={autoApproved as boolean}
                      tenderUsers={tenderUsers as UserOnEntity[]}
                    />
                  </div>
                );
              })}

            </>
          ) : loading ? (
            <div className="flex items-center justify-center h-[50vh]">
              <CircleLoader
                visible={true}
                height={80}
                width={80}
                strokeWidth={8}
                ariaLabel="circle-loader"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-[50vh]">
              <p className="text-sm text-gray-400">
                {t("chatwindow.nodata")}
              </p>
            </div>
          )}
          {/* div for anchor to auto scroll to bottom */}
          <div ref={scrollEndRef} />
        </div>
      </ScrollArea>
      <div className="space-y-2 pr-4 pl-2">
        {!loading &&
          <>
            <PendingDecisionBar
              marginDecisions={marginDecisions ?? []}
              marginDecisionGroup={marginDecisionGroupData}
            />
            {
              !hasFinalDecision && (
                <ChatInputBar
                  tenderState={data?.fetchMarginDecisionsByGroup.tenderState as TenderState}
                  marginDecisionGroupData={marginDecisionGroupData}
                  userHasPermission={userHasPermission ?? false}
                  tenderUsers={tenderUsers as UserOnEntity[]}
                />
              )
            }
          </>
        }
      </div>
    </div>
  );
};

export default MarginDecisionsComponent;
